import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { Loader2, RefreshCcw, WifiOff } from "lucide-react";
import { LogoMark } from "@/components/AppShell";
import { api, errMsg } from "@/lib/api";
import { tx, txt, useContent } from "@/content/ContentContext";

const INK = "#0F172A";
const AMBER = "#F59E0B";

// Rounded-dot QR with rounded eyes and a cleared centre for the logo (error correction H tolerates the hole).
export function QrArt({ value, size = 232 }) {
  const { n, dots, hole } = useMemo(() => {
    const m = QRCode.create(value, { errorCorrectionLevel: "H" }).modules;
    const n = m.size;
    let hole = Math.round(n * 0.24);
    if (hole % 2 !== n % 2) hole += 1;
    const h0 = (n - hole) / 2;
    const eye = (r, c) => (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
    const dots = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
      if (!m.get(r, c) || eye(r, c) || (r >= h0 - 0.5 && r < h0 + hole && c >= h0 - 0.5 && c < h0 + hole)) continue;
      dots.push([r, c]);
    }
    return { n, dots, hole };
  }, [value]);
  const q = 2;
  const eyes = [[0, 0], [0, n - 7], [n - 7, 0]];
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="qr-art" data-qr-value={value}>
      <svg viewBox={`${-q} ${-q} ${n + 2 * q} ${n + 2 * q}`} width="100%" height="100%" role="img" aria-label={tx("login.qr_aria")}>
        <rect x={-q} y={-q} width={n + 2 * q} height={n + 2 * q} fill="#fff" />
        {dots.map(([r, c]) => <circle key={`${r}-${c}`} cx={c + 0.5} cy={r + 0.5} r={0.46} fill={INK} />)}
        {eyes.map(([r, c]) => (
          <g key={`${r}-${c}`}>
            <rect x={c + 0.5} y={r + 0.5} width={6} height={6} rx={2} fill="none" stroke={INK} strokeWidth={1} />
            <rect x={c + 2} y={r + 2} width={3} height={3} rx={1} fill={AMBER} />
          </g>
        ))}
      </svg>
      <div className="absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-xl bg-white p-1" style={{ width: `${(100 * hole) / (n + 2 * q)}%`, height: `${(100 * hole) / (n + 2 * q)}%` }}>
        <LogoMark className="h-full w-full" />
      </div>
    </div>
  );
}

function Ring({ left, total }) {
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90" data-testid="qr-countdown-ring">
      <circle cx="18" cy="18" r={r} fill="none" stroke="#E2E8F0" strokeWidth="3" />
      <circle cx="18" cy="18" r={r} fill="none" stroke={AMBER} strokeWidth="3" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - left / total)} className="transition-[stroke-dashoffset] duration-300 ease-linear" />
    </svg>
  );
}

const MAX_AUTO = 5;

export function QrLoginPanel({ onSession, large }) {
  useContent();
  const [phase, setPhase] = useState("loading"); // loading | showing | paused | offline | approving | error
  const [code, setCode] = useState(null);
  const [error, setError] = useState("");
  const [clock, setClock] = useState(Date.now());
  const refreshes = useRef(0);
  const gen = useRef(0);
  const done = useRef(onSession);
  done.current = onSession;

  const start = useCallback(async (manual) => {
    if (manual) refreshes.current = 0;
    if (refreshes.current > MAX_AUTO) { setCode(null); return setPhase("paused"); }
    if (!navigator.onLine) { setCode(null); return setPhase("offline"); }
    const my = ++gen.current;
    setPhase("loading");
    try {
      const { data } = await api.post("/auth/qr/start");
      if (my !== gen.current) return;
      setCode({ ...data, until: Date.now() + data.ttl * 1000 });
      setPhase("showing");
    } catch (e) {
      if (my !== gen.current) return;
      setCode(null);
      if (!e.response) return setPhase("offline");
      setError(errMsg(e));
      setPhase("error");
    }
  }, []);

  useEffect(() => { start(true); }, [start]);

  useEffect(() => {
    const on = () => start(true);
    const off = () => { gen.current++; setCode(null); setPhase("offline"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [start]);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    const t = setInterval(() => setClock(Date.now()), 250);
    (async () => {
      while (alive) {
        try {
          const { data } = await api.get(`/auth/qr/${code.id}/status`, { params: { secret: code.secret, wait: 20 }, timeout: 30000 });
          if (!alive) return;
          if (data.status === "approved") { setPhase("approving"); done.current(data.session); return; }
          if (data.status === "denied") { setError(data.detail || ""); refreshes.current = 0; return start(); }
          if (data.status !== "pending") { refreshes.current += 1; return start(); }
        } catch (e) {
          if (!alive) return;
          if (!e.response) { setCode(null); return setPhase("offline"); }
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    })();
    return () => { alive = false; clearInterval(t); };
  }, [code, start]);

  const left = code ? Math.min(code.ttl, Math.max(0, Math.ceil((code.until - clock) / 1000))) : 0;
  return (
    <div className={`mx-auto rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35)] ${large ? "max-w-[420px] px-6 py-5" : "p-6"}`} data-testid="qr-login-panel" data-phase={phase}>
      <h3 className={`font-display font-bold text-slate-900 ${large ? "text-center text-lg" : "text-lg"}`} data-testid="qr-title">{txt("login.qr_title")}</h3>
      <div className={`relative mx-auto grid place-items-center rounded-2xl bg-white ring-1 ring-slate-100 ${large ? "mt-3" : "mt-5 h-[248px] w-[248px]"}`} style={large ? { width: "min(312px, calc(32vh + 16px))", height: "min(312px, calc(32vh + 16px))" } : undefined}>
        {phase === "showing" && code && <QrArt value={code.payload} size={large ? "min(296px, 32vh)" : 232} />}
        {phase === "approving" && <div className="flex flex-col items-center gap-2 text-sm font-semibold text-slate-600" data-testid="qr-logging-in"><Loader2 className="h-6 w-6 animate-spin text-amber-500" />{txt("login.qr_logging_in")}</div>}
        {phase === "loading" && <div className="flex flex-col items-center gap-2 text-sm text-slate-500" data-testid="qr-loading"><Loader2 className="h-6 w-6 animate-spin" />{txt("login.qr_loading")}</div>}
        {phase === "paused" && (
          <button onClick={() => start(true)} className="flex flex-col items-center gap-3 rounded-2xl p-6 text-sm font-bold text-slate-900" data-testid="qr-paused">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-amber-500 text-slate-950"><RefreshCcw className="h-5 w-5" /></span>{txt("login.qr_paused")}
          </button>
        )}
        {phase === "offline" && <div className="flex flex-col items-center gap-3 px-6 text-center text-sm font-semibold text-slate-600" data-testid="qr-offline"><WifiOff className="h-6 w-6 text-slate-400" />{txt("login.qr_offline")}</div>}
        {phase === "error" && (
          <button onClick={() => start(true)} className="flex flex-col items-center gap-3 px-6 text-center text-sm font-semibold text-rose-700" data-testid="qr-error">{error}<span className="text-slate-900 underline">{txt("login.qr_retry")}</span></button>
        )}
      </div>
      {phase === "showing" && (
        <div className={`${large ? "mt-2" : "mt-4"} flex items-center justify-center gap-2 text-sm text-slate-500`} data-testid="qr-countdown">
          <Ring left={left} total={code.ttl} /><span className="tabular-nums" data-testid="qr-countdown-text">{tx("login.qr_new_code_in", { seconds: left })}</span>
        </div>
      )}
      {error && phase === "showing" && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-center text-sm font-medium text-rose-700" data-testid="qr-note">{error}</div>}
      <ol className={`${large ? "mt-3 space-y-1" : "mt-5 space-y-2"} text-sm text-slate-600`} data-testid="qr-steps">
        {["login.qr_step1", "login.qr_step2", "login.qr_step3"].map((k, i) => (
          <li key={k} className="flex items-center gap-3"><span className={`grid shrink-0 place-items-center rounded-full bg-slate-900 font-mono text-[11px] font-bold text-amber-400 ${large ? "h-5 w-5" : "h-6 w-6"}`}>{i + 1}</span>{txt(k)}</li>
        ))}
      </ol>
    </div>
  );
}

export function qrError(e) {
  const s = e?.response?.status;
  if (s === 410) return tx("login.qr_err_expired");
  if (s === 409) return tx("login.qr_err_used");
  if (s === 400 || s === 404) return tx("login.qr_err_invalid");
  return errMsg(e);
}
